// Canonicalizes employee status values to the `employee_status` DB enum.
// The enum stores hyphenated/underscored values ("leave-of-absence",
// "pending_activation") but CSV import and UI free text use spaced/aliased
// forms ("Leave of absence", "Pending Activation"). Feeding a non-enum value
// straight into an INSERT throws and rolls back the whole bulk import, so any
// status headed for the DB must pass through here first.

// Valid `employee_status` enum values (keep in sync with employeeStatusEnum in
// shared/schemas/business.ts). Inlined to avoid a circular import — business.ts
// pulls from this utils folder.
export const EMPLOYEE_STATUS_ENUM_VALUES = [
  "active",
  "inactive",
  "terminated",
  "cobra",
  "leave-of-absence",
  "retired",
  "pending_activation",
] as const;

export type EmployeeStatusEnum = (typeof EMPLOYEE_STATUS_ENUM_VALUES)[number];

const VALID = new Set<string>(EMPLOYEE_STATUS_ENUM_VALUES);

// Aliases (lowercased, trimmed) -> canonical enum value.
const ALIAS_TO_ENUM: Record<string, EmployeeStatusEnum> = {
  active: "active",
  inactive: "inactive",
  terminated: "terminated",
  cobra: "cobra",
  retired: "retired",
  "leave-of-absence": "leave-of-absence",
  "leave of absence": "leave-of-absence",
  "leave_of_absence": "leave-of-absence",
  "on leave": "leave-of-absence",
  loa: "leave-of-absence",
  leave: "leave-of-absence",
  pending_activation: "pending_activation",
  "pending activation": "pending_activation",
  "pending-activation": "pending_activation",
  pending: "pending_activation",
};

/**
 * Map an arbitrary status string to a valid `employee_status` enum value.
 * Returns `fallback` (default "active") for empty or unrecognized input so a
 * stray value never breaks an import.
 */
export function normalizeEmployeeStatus(
  value: string | null | undefined,
  fallback: EmployeeStatusEnum = "active",
): EmployeeStatusEnum {
  if (value == null) return fallback;
  const key = String(value).trim().toLowerCase();
  if (!key) return fallback;
  if (VALID.has(key)) return key as EmployeeStatusEnum;
  return ALIAS_TO_ENUM[key] ?? fallback;
}
